import React, { useState, useEffect } from "react";
import ApiClient from "../services/api-client";
import { useTranslation } from 'react-i18next';
import VivaFooter from '../img/viva_footer.png';
import { VIVA_VIEW_TRANSACTION } from '../util/constants';
import { useNavigate } from 'react-router-dom';
import MyDataLogo from "../img/mydata.png";


const formatPrice = (amount, isYearly) => {
    const euros = isYearly ? (amount / 100 / 12 / 1.17) : (amount / 100 / 1.17);
    return euros.toLocaleString('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};


export default function StartSubscription() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [paymentMethod, setPaymentMethod] = useState('0');
    const [monthlyPackages, setMonthlyPackages] = useState([]);
    const [yearlyPackages, setYearlyPackages] = useState([]);

    useEffect(() => {
        ApiClient.getPaymentPackages().then(response => {
            const pkgs = response.data;
            setMonthlyPackages(pkgs.filter(p => p.paymentType === 'MONTHLY').map(p => ({
                id: p.name,
                invoices: p.displayInvoices,
                price: formatPrice(p.amount, false),
            })));
            setYearlyPackages(pkgs.filter(p => p.paymentType === 'YEARLY').map(p => ({
                id: p.name,
                invoices: p.displayInvoices,
                price: formatPrice(p.amount, true),
            })));
        }).catch(err => console.error(err));
    }, []);

    const createOrder = (_e, packId) => {
        if (paymentMethod === '0') {
            ApiClient.createReccuringPaymentOrder(packId).then(
                (response) => {
                    if (response.data.Success) {
                        window.location = VIVA_VIEW_TRANSACTION + "/web/checkout?ref=" + response.data.OrderCode;
                    }
                },
                error => console.error(error.response?.data?.message || error.message)
            );
        } else {
            ApiClient.createPaymentOrder(packId).then(
                (response) => {
                    if (response.data.Success) {
                        window.location = VIVA_VIEW_TRANSACTION + "/web/checkout?ref=" + response.data.OrderCode + "&paymentMethod=" + paymentMethod;
                    }
                },
                error => console.error(error.response?.data?.message || error.message)
            );
        }
    };

    const PricingCard = ({ pkg, isYearly }) => (
        <div className="subscription-card">
            <div className="subscription-card-badge">
                <img src={MyDataLogo} alt="myDATA" className="subscription-mydata-logo" />
            </div>
            <div className="subscription-card-invoices">
                <span className="subscription-invoice-count">
                    {pkg.invoices ? pkg.invoices.toLocaleString('el-GR') : '∞'}
                </span>
                <span className="subscription-invoice-label">
                    {pkg.invoices ? (isYearly ? 'τιμολόγια/έτος' : 'τιμολόγια/μήνα') : 'απεριόριστα'}
                </span>
            </div>
            <ul className="subscription-features">
                <li><i className="fa fa-check-circle"></i> myDATA / ΑΑΔΕ</li>
                <li><i className="fa fa-check-circle"></i> Αυτόματη μετάδοση</li>
                <li><i className="fa fa-check-circle"></i> PDF τιμολόγια</li>
            </ul>
            <button
                className="btn btn-skin subscription-cta-btn"
                onClick={e => createOrder(e, pkg.id)}
            >
                Ξεκινήστε με {pkg.price}
            </button>
        </div>
    );

    return (
        <div className="startSubscription-container">
            <div className="subscription-wrapper">

                {/* Header */}
                <div className="subscription-header text-center">
                    <div className="subscription-header-icon">
                        <i className="fa fa-rocket"></i>
                    </div>
                    <h2 className="subscription-title">Ξεκίνα σήμερα τη συνδρομή σου!</h2>
                    <p className="subscription-subtitle">
                        Επίλεξε το πλάνο που σου ταιριάζει. Οι συνδρομές ανανεώνονται αυτόματα εκτός κι αν ακυρωθούν.
                    </p>
                </div>

                {/* Tabs */}
                <ul className="nav justify-content-center subscription-tabs mb-4" id="subTab" role="tablist">
                    <li className="nav-item" role="presentation">
                        <button
                            className="nav-link active subscription-tab-btn"
                            id="monthly-tab"
                            data-bs-toggle="tab"
                            data-bs-target="#monthly-packs"
                            type="button"
                            role="tab"
                        >
                            {t("monthlyPacks")}
                        </button>
                    </li>
                    <li className="nav-item" role="presentation">
                        <button
                            className="nav-link subscription-tab-btn"
                            id="yearly-tab"
                            data-bs-toggle="tab"
                            data-bs-target="#yearly-packs"
                            type="button"
                            role="tab"
                        >
                            {t("yearlyPacks")}
                            <sup><span className="badge bg-success yearlyPercentDiscount">-18%</span></sup>
                        </button>
                    </li>
                </ul>

                <div className="tab-content">

                    {/* Monthly */}
                    <div className="tab-pane fade show active" id="monthly-packs" role="tabpanel">
                        <div className="row justify-content-center g-3 px-3">
                            {monthlyPackages.map(pkg => (
                                <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6" key={pkg.id}>
                                    <PricingCard pkg={pkg} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Yearly */}
                    <div className="tab-pane fade" id="yearly-packs" role="tabpanel">
                        <p className="text-center subscription-yearly-note">
                            <i className="fa fa-tag" style={{ marginRight: 6 }}></i>
                            Οι παρακάτω τιμές αναφέρονται σε 12 μήνες συνδρομή
                        </p>
                        <div className="row justify-content-center g-3 px-3">
                            {yearlyPackages.map(pkg => (
                                <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6" key={pkg.id}>
                                    <PricingCard pkg={pkg} isYearly />
                                </div>
                            ))}
                        </div>
                    </div>

                </div>

                {/* Payment method + VAT note */}
                <div className="subscription-payment-row">
                    <div className="subscription-payment-selector">
                        <label className="inputlabel">
                            <i className="fa fa-credit-card" style={{ marginRight: 6 }}></i>
                            Τρόπος πληρωμής
                        </label>
                        <select
                            value={paymentMethod}
                            onChange={e => setPaymentMethod(e.target.value)}
                            className="form-select subscription-payment-select"
                        >
                            <option value="0">ΚΑΡΤΑ (προτεινόμενος)</option>
                            <option value="3">ΜΕΤΡΗΤΑ</option>
                            <option value="4">e-banking</option>
                        </select>
                    </div>
                    <p className="subscription-vat-note">* Στις τιμές δεν περιλαμβάνεται Φ.Π.Α.</p>
                </div>

                {/* Footer */}
                <div className="subscription-footer">
                    <p className="subscription-legal">
                        Γίνονται δεκτές πιστωτικές ή χρεωστικές κάρτες Visa, Mastercard ή American Express, μετρητά ή e-banking.
                        Οι συναλλαγές πραγματοποιούνται μέσω του αδειοδοτημένου ιδρύματος πληρωμών <strong>Viva Payments</strong>,
                        εγγυώμενο ασφαλές περιβάλλον. Τα στοιχεία της κάρτας σας δεν αποθηκεύονται στους διακομιστές μας.
                    </p>
                    <a
                        href="https://timologic.gr/blog/tropoi-pliromis/"
                        target="_blank"
                        rel="noreferrer"
                        className="subscription-payment-link"
                    >
                        <i className="fa fa-external-link" style={{ marginRight: 6 }}></i>
                        Τρόποι πληρωμής
                    </a>
                    <div className="subscription-viva-logo">
                        <img src={VivaFooter} alt="Viva Payments" />
                    </div>
                    <button onClick={() => navigate(-1)} className="btn btn-outline-secondary btn-sm mt-2">
                        <i className="fa fa-arrow-left" style={{ marginRight: 6 }}></i>
                        {t("goBackToApplication")}
                    </button>
                </div>

            </div>
        </div>
    );
}
